import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ActionIcon, Box, Group, Indicator, Popover, ScrollArea, Stack, Text } from "@mantine/core";
import { Bell, Eye, EyeOff } from "lucide-react";
import { AnnouncementCorner, applyTakeoverSuppression } from "@nodus/announcements";
import { useAnnouncements } from "@/hooks/use-announcements";
import { handleRowToggleKey } from "@/lib/announcement-row-key";
import { useAnnouncementDismissal } from "@/lib/announcements-dismissal";
import { AnnouncementSoundToggle } from "@/components/announcement-sound";

/**
 * Header bell + badge showing the count of currently-active announcements,
 * opening a panel that lists them all — including ones dismissed from the
 * banner/corner/fullscreen surfaces. Each row is a show/hide toggle: a
 * dismissed announcement shows dimmed with "tap to show", and clicking it
 * restores it to its surface. So "dismiss" never loses anything. Hidden when
 * none active.
 */
export function AnnouncementsIndicator() {
  const announcements = applyTakeoverSuppression(useAnnouncements());
  const { isDismissed, dismiss, restore } = useAnnouncementDismissal();
  const count = announcements.length;
  const navigate = useNavigate();
  const [opened, setOpened] = useState(false);

  if (count === 0) return null;

  return (
    <Popover width={360} position="bottom-end" withArrow shadow="md" opened={opened} onChange={setOpened}>
      <Popover.Target>
        <Indicator label={count} size={16} color="red" offset={4}>
          {/* The popover is controlled (a link inside closes it), so the bell must toggle it itself. */}
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label="Announcements"
            aria-expanded={opened}
            onClick={() => setOpened((o) => !o)}
          >
            <Bell size={18} />
          </ActionIcon>
        </Indicator>
      </Popover.Target>
      <Popover.Dropdown p="xs">
        <Text fz="xs" c="dimmed" mb="xs" px={4}>
          Active announcements · tap to show or hide
        </Text>
        <Box px={4} mb="xs">
          <AnnouncementSoundToggle />
        </Box>
        <ScrollArea.Autosize mah={420}>
          <Stack gap="sm">
            {announcements.map((a) => {
              const hidden = isDismissed(a);
              return (
                <Box
                  key={a.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => (hidden ? restore(a.id) : dismiss(a))}
                  onKeyDown={(e) => handleRowToggleKey(e, () => (hidden ? restore(a.id) : dismiss(a)))}
                  style={{ cursor: "pointer", opacity: hidden ? 0.55 : 1, transition: "opacity 120ms" }}
                  aria-label={hidden ? "Show announcement" : "Hide announcement"}
                >
                  <AnnouncementCorner
                    announcement={a}
                    onOpen={(path) => {
                      setOpened(false);
                      navigate(path);
                    }}
                  />
                  <Group gap={4} justify="flex-end" mt={4} px={4}>
                    {hidden ? <EyeOff size={12} /> : <Eye size={12} />}
                    <Text fz="xs" c="dimmed">
                      {hidden ? "Hidden · tap to show" : "Showing · tap to hide"}
                    </Text>
                  </Group>
                </Box>
              );
            })}
          </Stack>
        </ScrollArea.Autosize>
      </Popover.Dropdown>
    </Popover>
  );
}
